/**
 * useBatchOCR - Hook pour l'OCR batch multi-fichiers
 *
 * Pattern: Component → Hook → Service (batchOCRAPI)
 * Convertit N fichiers en base64, envoie au backend, retourne N résultats OCR.
 */

import { useState, useCallback } from 'react';
import { batchOCRAPI } from '@/utils/api';
import { logger } from '@/utils/logger';
import { notify } from '@/stores/notificationStore';

export interface OCRResultItem {
  index: number;
  filename: string;
  success: boolean;
  data: Record<string, unknown> | null;
  confidenceScore: number;
  processingTimeMs: number;
  error?: string;
}

export interface BatchOCRState {
  isProcessing: boolean;
  progress: number;
  total: number;
  results: OCRResultItem[];
  error: string | null;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function useBatchOCR() {
  const [state, setState] = useState<BatchOCRState>({
    isProcessing: false,
    progress: 0,
    total: 0,
    results: [],
    error: null,
  });

  const processFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    if (files.length > 20) {
      notify.warning('Maximum 20 fichiers par import');
      return;
    }

    setState({
      isProcessing: true,
      progress: 0,
      total: files.length,
      results: [],
      error: null,
    });

    try {
      // Détecter si c'est un PDF unique (potentiellement multi-factures) ou multi-fichiers
      const isSinglePdf = files.length === 1 && files[0].type === 'application/pdf';

      let response: Record<string, unknown>;

      if (isSinglePdf) {
        // Phase 2 : Split PDF multi-factures
        const documentUrl = await fileToBase64(files[0]);
        setState((prev) => ({ ...prev, progress: 1 }));
        response = await batchOCRAPI.splitAndExtract(documentUrl);
      } else {
        // Phase 1 : Multi-fichiers, OCR chaque fichier séparément
        const documents = await Promise.all(
          files.map(async (file) => ({
            documentUrl: await fileToBase64(file),
            documentType: 'invoice' as const,
            filename: file.name,
          }))
        );
        setState((prev) => ({ ...prev, progress: 1 }));
        response = await batchOCRAPI.batchExtract(documents);
      }

      const rawResults = (response.results || []) as Record<string, unknown>[];
      const results: OCRResultItem[] = rawResults.map((r, i: number) => ({
        index: i,
        filename: isSinglePdf
          ? (r.filename as string || `facture-${i + 1}`)
          : (files[i]?.name || `document-${i}`),
        success: r.success as boolean,
        data: r.data as Record<string, unknown> | null,
        confidenceScore: (r.confidenceScore || 0) as number,
        processingTimeMs: (r.processingTimeMs || 0) as number,
        error: r.error as string | undefined,
      }));

      setState({
        isProcessing: false,
        progress: results.length,
        total: results.length,
        results,
        error: null,
      });

      const succeeded = results.filter((r) => r.success).length;
      notify.success(`${succeeded}/${files.length} factures extraites`);
    } catch (error: any) {
      logger.error('Batch OCR error', { error });
      setState((prev) => ({
        ...prev,
        isProcessing: false,
        error: error.message || "Erreur lors de l'extraction OCR",
      }));
      notify.error(error.message || "Erreur lors de l'extraction OCR");
    }
  }, []);

  const reset = useCallback(() => {
    setState({
      isProcessing: false,
      progress: 0,
      total: 0,
      results: [],
      error: null,
    });
  }, []);

  return {
    ...state,
    processFiles,
    reset,
  };
}
